import { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Copy, RefreshCw, ArrowRightLeft } from 'lucide-react';
import { toast } from 'sonner';

const RomanNumeralsTool = () => {
  const [arabic, setArabic] = useState('');
  const [roman, setRoman] = useState('');
  const [error, setError] = useState('');

  const arabicToRoman = (num) => {
    if (num < 1 || num > 3999) return null;
    
    const romanNumerals = [
      ['M', 1000], ['CM', 900], ['D', 500], ['CD', 400],
      ['C', 100], ['XC', 90], ['L', 50], ['XL', 40],
      ['X', 10], ['IX', 9], ['V', 5], ['IV', 4], ['I', 1]
    ];

    let result = '';
    for (const [roman, value] of romanNumerals) {
      while (num >= value) {
        result += roman;
        num -= value;
      }
    }
    return result;
  };

  const romanToArabic = (str) => {
    const romanValues = { I: 1, V: 5, X: 10, L: 50, C: 100, D: 500, M: 1000 };
    let result = 0;
    const upper = str.toUpperCase();
    
    for (let i = 0; i < upper.length; i++) {
      const current = romanValues[upper[i]];
      const next = romanValues[upper[i + 1]];
      
      if (!current) return null;
      
      if (next && current < next) {
        result -= current;
      } else {
        result += current;
      }
    }
    return result;
  };

  const handleArabicChange = (value) => {
    setArabic(value);
    setError('');
    
    const num = parseInt(value);
    if (!value) {
      setRoman('');
      return;
    }
    if (isNaN(num) || num < 1 || num > 3999) {
      setError('Enter a number between 1 and 3999');
      setRoman('');
      return;
    }
    
    setRoman(arabicToRoman(num));
  };

  const handleRomanChange = (value) => {
    setRoman(value.toUpperCase());
    setError('');
    
    if (!value) {
      setArabic('');
      return;
    }
    
    const result = romanToArabic(value);
    if (result === null) {
      setError('Invalid Roman numeral');
      setArabic('');
      return;
    }
    
    setArabic(result.toString());
  };

  const copyValue = (val) => {
    navigator.clipboard.writeText(val);
    toast.success('Copied!');
  };

  const examples = [
    { arabic: 1, roman: 'I' },
    { arabic: 5, roman: 'V' },
    { arabic: 10, roman: 'X' },
    { arabic: 50, roman: 'L' },
    { arabic: 100, roman: 'C' },
    { arabic: 500, roman: 'D' },
    { arabic: 1000, roman: 'M' },
    { arabic: 2024, roman: 'MMXXIV' },
  ];

  return (
    <div className="space-y-6" data-testid="roman-numerals-tool">
      <div className="grid md:grid-cols-3 gap-4 items-center">
        <div>
          <label className="text-sm text-muted-foreground mb-2 block">Arabic Number</label>
          <div className="flex gap-2">
            <Input
              type="number"
              value={arabic}
              onChange={(e) => handleArabicChange(e.target.value)}
              placeholder="2024"
              className="font-mono"
              min={1}
              max={3999}
            />
            <Button onClick={() => copyValue(arabic)} variant="outline" size="icon" disabled={!arabic}>
              <Copy className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div className="flex justify-center">
          <ArrowRightLeft className="w-6 h-6 text-muted-foreground" />
        </div>

        <div>
          <label className="text-sm text-muted-foreground mb-2 block">Roman Numeral</label>
          <div className="flex gap-2">
            <Input
              value={roman}
              onChange={(e) => handleRomanChange(e.target.value)}
              placeholder="MMXXIV"
              className="font-mono uppercase"
            />
            <Button onClick={() => copyValue(roman)} variant="outline" size="icon" disabled={!roman}>
              <Copy className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {error && <p className="text-red-400 text-sm text-center">{error}</p>}

      <div className="p-4 rounded-xl bg-muted/30 border border-border">
        <h3 className="font-semibold mb-3">Reference</h3>
        <div className="grid grid-cols-4 md:grid-cols-8 gap-2">
          {examples.map(({ arabic, roman }) => (
            <button
              key={arabic}
              onClick={() => handleArabicChange(arabic.toString())}
              className="p-2 rounded bg-background hover:bg-primary/10 transition-colors text-center"
            >
              <span className="block font-mono text-primary">{roman}</span>
              <span className="block text-xs text-muted-foreground">{arabic}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="flex gap-2 justify-center">
        <Button onClick={() => { setArabic(''); setRoman(''); setError(''); }} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" /> Clear
        </Button>
      </div>
    </div>
  );
};

export default RomanNumeralsTool;
